<script lang="ts" setup>
import { useRouter } from 'vue-router';
import LicensePlateRender from '@/components/license-plate/LicensePlateRender.vue';
import { useI18n } from 'vue-i18n';
import { NButton, NIcon, NTooltip } from 'naive-ui';
import { Open16Regular } from '@vicons/fluent';
import type { PropType } from 'vue';
import type { VehicleIdentifierPlateSize } from '@/constants/vehicle-identifier-plate-size';
import { Country } from "@/constants/country";

const router = useRouter();
const { t } = useI18n();

//We assume the licenseplate formatting has been applied by the API when a vehicle is created
const props = defineProps({
  licensePlate: {
    type: String,
    required: false,
  },
  hasLinkToAssignment: {
    type: Boolean,
    required: false,
    default: false,
  },
  assignmentId: {
    type: Number,
    required: false,
  },
  hasLinkToVehicle: {
    type: Boolean,
    required: false,
    default: false,
  },
  vehicleId: {
    type: Number,
    required: false,
  },
  size: {
    type: String as PropType<VehicleIdentifierPlateSize>,
    default: 'medium',
    required: false,
  },
  country: {
    type: Number as PropType<Country>,
    required: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
    required: false,
  },
});

const showAssignmentLink = () =>
    props.hasLinkToAssignment && !!props.assignmentId && !props.readOnly;

const showVehicleLink = () =>
    props.hasLinkToVehicle && !!props.vehicleId && !props.readOnly;

const navigateToAssignment = () => {
  if (!props.hasLinkToAssignment || !props.assignmentId) {
    return;
  }
  const routeData = router.resolve(`/assignment/${props.assignmentId}`);
  window.open(routeData.href, '_blank');
};

const navigateToVehicle = () => {
  if (!props.hasLinkToVehicle || !props.vehicleId) {
    return;
  }
  router.push(`/vehicle/${props.vehicleId}`);
};
</script>

<template>
  <div class="license-plate-button-root">
    <license-plate-render
        :license-plate="props.licensePlate"
        :country="props.country"
        :size="props.size"
        :read-only="props.readOnly"
    />
    <n-tooltip v-if="showAssignmentLink()">
      <template #trigger>
        <n-button
            class="license-plate-open-assignment-button"
            circle
            size="small"
            quaternary
            @click.stop.prevent="navigateToAssignment"
        >
          <template #icon>
            <n-icon>
              <Open16Regular />
            </n-icon>
          </template>
        </n-button>
      </template>
      {{ t('components.license_plate.tooltip.open_assignment') }}
    </n-tooltip>
    <n-tooltip v-if="showVehicleLink()">
      <template #trigger>
        <n-button
            class="license-plate-open-vehicle-button"
            circle
            size="small"
            quaternary
            @click.stop.prevent="navigateToVehicle"
        >
          <template #icon>
            <n-icon>
              <Open16Regular />
            </n-icon>
          </template>
        </n-button>
      </template>
      {{ t('components.license_plate.tooltip.open_vehicle') }}
    </n-tooltip>
  </div>
</template>

<style lang="scss" scoped>
.license-plate-button-root {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: var(--dis-space-0_5x, 4px);
}

.license-plate-button-root > :deep(.vehicle-identification-plate-wrapper) {
  flex: 0 0 auto;
}

.license-plate-open-assignment-button,
.license-plate-open-vehicle-button {
  flex: 0 0 auto;
}
</style>
